import * as XLSX from 'node-xlsx';

export type DownloadFormat = 'csv' | 'xlsx' | 'xls';

export interface DownloadOptions {
  filename: string;
  format: DownloadFormat;
}

export interface WorksheetData {
  name: string;
  data: string[][];
  options?: XLSX.WorkSheetOptions;
}

export interface AdvancedDownloadOptions extends DownloadOptions {
  worksheets?: WorksheetData[];
}

export const downloadBlob = (blob: Blob, filename: string): void => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.visibility = 'hidden';
  document.body.append(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

export const downloadCsv = (data: string[][], filename: string): void => {
  const csvContent = data.map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  downloadBlob(blob, filename);
};

export const downloadSpreadsheet = (data: string[][], { filename, format }: DownloadOptions): void => {
  if (format === 'csv') {
    downloadCsv(data, filename);
    return;
  }

  const worksheetData: XLSX.WorkSheet[] = [
    {
      name: 'Sheet1',
      data: data,
      options: {},
    },
  ];

  const xlsxBuffer = XLSX.build(worksheetData);

  const mimeType =
    format === 'xlsx'
      ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      : 'application/vnd.ms-excel';

  const uint8 = Uint8Array.from(xlsxBuffer);
  const blob = new Blob([uint8], { type: mimeType });
  downloadBlob(blob, filename);
};

export const downloadAdvancedSpreadsheet = (
  data: string[][],
  { filename, format, worksheets }: AdvancedDownloadOptions
): void => {
  if (format === 'csv') {
    downloadCsv(data, filename);
    return;
  }

  const workbookData: XLSX.WorkSheet[] = worksheets?.map(sheet => ({
    ...sheet,
    options: sheet.options || {},
  })) || [
    {
      name: 'Sheet1',
      data: data,
      options: {},
    },
  ];

  const xlsxBuffer = XLSX.build(workbookData);

  const mimeType =
    format === 'xlsx'
      ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      : 'application/vnd.ms-excel';

  const uint8 = Uint8Array.from(xlsxBuffer);
  const blob = new Blob([uint8], { type: mimeType });
  downloadBlob(blob, filename);
};

export const downloadFromSource = async (
  source: Response | { data: BlobPart; headers: Record<string, string | undefined> },
  options: {
    filename?: string;
    autoDetectFilename?: boolean;
    fallbackFilename?: string;
  } = {}
): Promise<void> => {
  const { filename, autoDetectFilename = false, fallbackFilename = 'download' } = options;

  if (source instanceof Response) {
    if (!source.ok) {
      throw new Error(`Download failed: ${source.status}`);
    }

    let finalFilename = filename;

    if (!finalFilename && autoDetectFilename) {
      const headers: Record<string, string> = {};
      for (const [key, value] of source.headers) {
        headers[key] = value;
      }
      finalFilename = extractFilenameFromHeaders(headers) || fallbackFilename;
    }

    const blob = await source.blob();
    downloadBlob(blob, finalFilename || fallbackFilename);
  } else {
    const { data, headers } = source;

    let finalFilename = filename;

    if (!finalFilename && autoDetectFilename) {
      finalFilename = extractFilenameFromHeaders(headers) || fallbackFilename;
    }

    const mimeType = headers['content-type'] || headers['Content-Type'] || 'application/octet-stream';
    const blob = new Blob([data], { type: mimeType });
    downloadBlob(blob, finalFilename || fallbackFilename);
  }
};

export const createDateFilename = (baseName: string, extension: string): string => {
  const today = new Date().toISOString().split('T')[0];
  return `${baseName}-${today}.${extension}`;
};

export const extractFilenameFromHeaders = (headers: Record<string, string | undefined>): string | undefined => {
  const contentDisposition = headers['content-disposition'] || headers['Content-Disposition'];
  if (contentDisposition) {
    const filenameRegex = /filename="?([^";]+)"?/;
    const match = filenameRegex.exec(contentDisposition);
    if (match?.[1]) {
      return match[1];
    }
  }
  return undefined;
};
